import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import AlarmCard from '../alarms/AlarmCard';

/**
 * Renders item alarm rules in the existing alarm card format for the read-only summary drawer.
 */
const ItemAlarmManagementSummary = ({ item, allTags = [] }) => {
    const zones = item?.zones || [];
    const alarmsRules = item?.alarms_rules || [];
    const trafficLights = item?.traffic_lights || [];

    const normalizedAlarmRules = useMemo(() => {
        const resolveTag = (value) => {
            if (value === undefined || value === null || value === '') {
                return null;
            }

            const normalizedValue = String(value).toLowerCase();

            return allTags.find((tag) => {
                const idMatch = String(tag?.id) === String(value);
                const nameMatch = String(tag?.name || '').toLowerCase() === normalizedValue;

                return idMatch || nameMatch;
            }) || null;
        };

        const buildFallbackDisplayObject = (entry, missing = false) => {
            const classValues = Array.isArray(entry) ? entry : [entry];
            const classTags = classValues.map((value) => resolveTag(value)).filter(Boolean);
            const classNames = classTags.length > 0
                ? classTags.map((tag) => tag.name)
                : classValues.map((value) => String(value));

            return {
                classes: classNames,
                classes_id: classTags.length > 0 ? classTags.map((tag) => tag.id) : classValues,
                missing,
                value: classNames.join('-'),
            };
        };

        return (Array.isArray(alarmsRules) ? alarmsRules : [])
            .map((rule) => {
                if (!rule) {
                    return null;
                }

                const zoneId = Number(rule?.zone_id);
                const zone = Number.isInteger(zoneId) && zoneId > 0 ? zones[zoneId - 1] : null;
                const zoneAnalyzers = Array.isArray(zone?.advanced_settings?.analyzers) ? zone.advanced_settings.analyzers : [];
                const displayObjects = Array.isArray(rule?.displayObjectsData) && rule.displayObjectsData.length > 0
                    ? rule.displayObjectsData
                    : [
                        ...(Array.isArray(rule?.objects) ? rule.objects : []).map((entry) => buildFallbackDisplayObject(entry, false)),
                        ...(Array.isArray(rule?.missing_objects) ? rule.missing_objects : []).map((entry) => buildFallbackDisplayObject(entry, true)),
                    ];

                const objects = displayObjects.map((entry) => {
                    const classIds = Array.isArray(entry?.classes_id)
                        ? entry.classes_id
                        : (entry?.classes_id !== undefined && entry?.classes_id !== null ? [entry.classes_id] : []);
                    const classNames = Array.isArray(entry?.classes)
                        ? entry.classes
                        : (entry?.classes !== undefined && entry?.classes !== null ? [entry.classes] : []);
                    const primaryTag = resolveTag(classIds[0] ?? classNames[0]);
                    const extraTags = (classIds.length > 1 ? classIds.slice(1) : classNames.slice(1))
                        .map((value) => resolveTag(value))
                        .filter(Boolean);
                    const detectorIds = Array.isArray(entry?.detectorTags)
                        ? entry.detectorTags
                        : (entry?.detectorTag !== undefined && entry?.detectorTag !== null ? [entry.detectorTag] : []);
                    const detectorPrimary = resolveTag(detectorIds[0]);
                    const detectorExtra = detectorIds.slice(1).map((value) => resolveTag(value)).filter(Boolean);
                    const detector = detectorPrimary || detectorExtra.length > 0 || entry?.detectorText
                        ? {
                            value: entry?.detectorValue,
                            text: entry?.detectorText || detectorPrimary?.name || '',
                            tag: detectorPrimary,
                            tags: detectorExtra,
                        }
                        : null;

                    return {
                        value: entry?.value,
                        missing: Boolean(entry?.missing),
                        detector,
                        object: {
                            value: entry?.value,
                            text: classNames.join('-') || String(entry?.value || ''),
                            tag: primaryTag || { name: classNames[0] || String(classIds[0] || entry?.value || '') },
                            tags: extraTags,
                            classes: classIds.length > 0 ? classIds : classNames,
                        },
                    };
                });

                const displayAnalysis = Array.isArray(rule?.displayAnalysisData)
                    ? rule.displayAnalysisData
                    : Object.values(rule?.analysis || {}).flatMap((analysisList) => Array.isArray(analysisList) ? analysisList : []);

                const analysis = displayAnalysis.map((entry) => {
                    const analysisClassId = entry?.class_id ?? entry?.class ?? entry?.clasa ?? null;
                    const analysisInfo = zoneAnalyzers.find((analyzer) => {
                        const analyzerClassId = analyzer?.class_id ?? analyzer?.class ?? analyzer?.clasa ?? null;

                        return String(analyzer?.name || '') === String(entry?.name || '')
                            && String(analyzer?.model || '') === String(entry?.model || '')
                            && String(analyzerClassId || '') === String(analysisClassId || '');
                    }) || null;
                    const classTag = resolveTag(analysisInfo?.class_id ?? analysisInfo?.class ?? analysisInfo?.clasa ?? analysisClassId);

                    return {
                        analysisData: {
                            ...(analysisInfo || {}),
                            class_id: analysisInfo?.class_id ?? analysisInfo?.class ?? analysisInfo?.clasa ?? analysisClassId ?? classTag?.id ?? 0,
                            class_name: classTag?.name || entry?.class_name || String(analysisClassId || ''),
                            tag: classTag,
                            name: analysisInfo?.name || entry?.name || '',
                            model: analysisInfo?.model || entry?.model || '',
                        },
                        detail: Array.isArray(entry?.value) ? entry.value.join(', ') : (entry?.value ?? entry?.detail ?? ''),
                        limit_cumulate: Boolean(entry?.limit_cumulate),
                    };
                }).filter((entry) => entry.analysisData?.name || entry.detail);

                return {
                    version: rule?.version || 1,
                    id: rule?.rule_id,
                    rule: rule?.rule || `Alarm ${rule?.rule_id || ''}`,
                    hasMissingObjects: objects.some((entry) => entry.missing),
                    status: rule?.status === 'inactive' ? 'inactive' : 'active',
                    notifications_enabled: rule?.notifications_enabled !== false,
                    min_duration: Number(rule?.min_duration) || 0,
                    text_reasoning: rule?.text_reasoning || '',
                    audio_message: rule?.audio_message || '',
                    traffic_light_actions: rule?.traffic_light_actions || [],
                    traffic_lights: trafficLights.filter((light) => (zone?.meta?.traffic_light_ids || []).map(Number).includes(Number(light.id))),
                    alarm_frequency: Number(rule?.alarm_frequency) || 0,
                    threshold: Number(rule?.threshold) || 0,
                    zone_id: zoneId,
                    zone: Number.isInteger(zoneId) && zoneId > 0 ? `Zona ${zoneId}` : 'Entire zone',
                    objects,
                    analysis,
                };
            })
            .filter((rule) => rule && (rule.objects.length > 0 || rule.analysis.length > 0));
    }, [allTags, alarmsRules, zones, trafficLights]);

    if (!item) {
        return null;
    }

    if (normalizedAlarmRules.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No active alarm management rules configured for this item.
            </Typography>
        );
    }

    return (
        <Box>
            {normalizedAlarmRules.map((alarmRule) => (
                <AlarmCard
                    key={`item-alarm-summary-${alarmRule.id || alarmRule.rule}`}
                    alarm={alarmRule}
                    trafficLights={alarmRule.traffic_lights}
                    disabled
                />
            ))}
        </Box>
    );
};

export default ItemAlarmManagementSummary;